import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  Observable,
  catchError,
  combineLatest,
  defaultIfEmpty,
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
  take,
} from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import {
  CityToCityPairResponse,
  CmsContent,
  CmsContentType,
  CmsImageData,
  CmsTaxonomy,
  CmsTeaser,
  CmsTeaserCard,
  CmsTemplate,
  CmsViewType,
} from '@fcom/core-api';
import { LocationRouteCffService } from '@fcom/core';
import { CmsDataService } from '@fcom/core/services/cms/cms-data.service';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { isDeepEqual } from '@fcom/core/utils/utils';
import { ReplaceParams } from '@fcom/core/utils/replaceMsgFormat';
import { HaulType } from '@fcom/common/interfaces/gtm.interface';
import { resolveHaulType, resolveTripTypeByHaul } from '@fcom/common/utils/flight.utils';
import { finShare } from '@fcom/rx';
import {
  DestinationTimetableInfo,
  PriceCalendarService,
} from '@fcom/common/services/price-calendar/price-calendar.service';
import { findByType } from '@fcom/cms/utils/utils';
import { initialState as globalBookingInitialState } from '@fcom/common/store/reducers/global-booking.reducer';

import {
  DestinationPromotionCopy,
  FlightPair,
  OndLocationsResponse,
  OndStructurePagePath,
  StructurePageBlockType,
} from '../interfaces/ond-marketing-offers.interface';
import { OndMarketingOffersService } from './ond-marketing-offers.service';
import { OndMarketingSeoService } from './ond-marketing-seo.service';
import { buildDestinationPromoCopy, constructOndHeadingPath, findFirstCmsNode, getLocationName } from './utils';

export type CmsTemplateLoader = (path: string) => Observable<CmsTemplate>;

const BLUEWINGS_SUBJECT_TAG = 'destinations' as CmsTaxonomy;
const BLUEWINGS_STORY_LIMIT = 5;
const DESTINATION_PROMO_IMAGE_CONTENT_TYPE = 'seo-destination-image';
const EMPTY_TIMETABLE_INFO: DestinationTimetableInfo = { airportCodes: [], isDirect: false };

/**
 * Populates the city-to-city CMS `structure` template with route-derived data.
 */
@Injectable()
export class OndMarketingTemplateService implements OnDestroy {
  private route = inject(ActivatedRoute);
  private ondMarketingSeoService = inject(OndMarketingSeoService);
  private languageService = inject(LanguageService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private ondMarketingOffersService = inject(OndMarketingOffersService);
  private priceCalendarService = inject(PriceCalendarService);
  private cmsDataService = inject(CmsDataService);

  private patchedNodes = new WeakMap<CmsContent, CmsContent>();

  private flightParams$: Observable<FlightPair> = this.route.params.pipe(
    distinctUntilChanged(
      (a, b) => a.originLocationCode === b.originLocationCode && a.destinationLocationCode === b.destinationLocationCode
    ),
    switchMap((params) => this.ondMarketingSeoService.getFlightParams(params, this.languageService.langKeyValue)),
    finShare()
  );

  private tripType$: Observable<TripType> = this.flightParams$.pipe(
    switchMap(({ origin, destination }) =>
      resolveTripTypeByHaul(this.locationRouteCffService, origin, destination, this.languageService.localeValue)
    ),
    finShare()
  );

  private pricesAvailable$: Observable<boolean | null> = combineLatest([this.flightParams$, this.tripType$]).pipe(
    switchMap(([{ origin, destination }, tripType]) => {
      if (!origin || !destination) {
        return of(null);
      }
      return this.ondMarketingOffersService.pricesAvailable$({ origin, destination, tripType });
    }),
    finShare()
  );

  private haul$: Observable<HaulType | null> = this.flightParams$.pipe(
    switchMap(({ origin, destination }) =>
      resolveHaulType(this.locationRouteCffService, origin, destination, this.languageService.localeValue)
    ),
    finShare()
  );

  private timetableInfo$: Observable<DestinationTimetableInfo> = this.flightParams$.pipe(
    switchMap(({ origin, destination }) => {
      return this.priceCalendarService
        .getDestinationTimetableInfo(origin, destination)
        .pipe(startWith(EMPTY_TIMETABLE_INFO));
    }),
    finShare()
  );

  private heading$: Observable<string> = this.flightParams$.pipe(
    switchMap((flightParams) =>
      this.ondMarketingSeoService.getLocations(flightParams).pipe(
        defaultIfEmpty<OndLocationsResponse, OndLocationsResponse>({}),
        switchMap((locationData) => {
          const translationData: ReplaceParams = {
            origin: getLocationName(flightParams.origin, locationData),
            destination: getLocationName(flightParams.destination, locationData),
            originLocationCode: flightParams.origin,
            destinationLocationCode: flightParams.destination,
          };
          return this.languageService.translate<string>(
            constructOndHeadingPath('bookFlights', flightParams),
            translationData
          );
        })
      )
    ),
    distinctUntilChanged(),
    finShare()
  );

  private faqs$: Observable<CmsContent[]> = this.flightParams$.pipe(
    switchMap((flightParams) => {
      const { origin, destination } = flightParams;

      if (!origin || !destination) {
        return of<CmsContent[]>([]);
      }

      return this.locationRouteCffService
        .getCityToCityPair(origin, destination, this.languageService.localeValue)
        .pipe(map((response) => this.buildFaqItems(response)));
    }),
    finShare()
  );

  private bluewingsStories$: Observable<CmsTeaserCard[]> = this.flightParams$.pipe(
    switchMap(({ destination }) => {
      if (!destination) {
        return of<CmsTeaserCard[]>([]);
      }

      return this.cmsDataService
        .getArticleListByTaxonomyTags({
          lang: this.languageService.langValue,
          locale: this.languageService.localeValue,
          taxonomyTags: [BLUEWINGS_SUBJECT_TAG],
          location: destination,
          limit: BLUEWINGS_STORY_LIMIT,
        })
        .pipe(map(({ articles }) => articles));
    }),
    finShare()
  );

  ngOnDestroy(): void {
    this.priceCalendarService.clearKnownNoPrices();
  }

  populateCityToCityTemplate(
    template$: Observable<CmsTemplate>,
    loadCmsPage: CmsTemplateLoader
  ): Observable<CmsTemplate> {
    return combineLatest([
      template$,
      this.flightParams$,
      this.heading$,
      this.tripType$,
      this.haul$,
      this.destinationPromo$(loadCmsPage),
      this.faqs$,
      this.flightInfoPicture$(loadCmsPage),
      this.pricesAvailable$,
      this.bluewingsStories$,
    ]).pipe(
      map(
        ([
          template,
          flightParams,
          heading,
          tripType,
          haul,
          destinationPromo,
          faqs,
          flightInfoPicture,
          pricesAvailable,
          bluewings,
        ]) => ({
          ...template,
          main: this.patchNodes(
            template.main,
            flightParams,
            heading,
            tripType,
            haul,
            destinationPromo,
            faqs,
            flightInfoPicture,
            pricesAvailable,
            bluewings
          ),
        })
      ),
      finShare()
    );
  }

  private destinationPromo$(loadCmsPage: CmsTemplateLoader): Observable<CmsTeaser | null> {
    const promoPicture$ = this.promoPicture$(loadCmsPage);

    return this.flightParams$.pipe(
      switchMap((flightParams) => {
        const destinationCode = flightParams.destination;

        if (!destinationCode) {
          return of(null);
        }

        return combineLatest([
          this.ondMarketingSeoService
            .getLocations(flightParams)
            .pipe(defaultIfEmpty<OndLocationsResponse, OndLocationsResponse>({})),
          this.timetableInfo$,
          promoPicture$,
        ]).pipe(
          switchMap(([locationData, timetableInfo, picture]) => {
            const destinationUrl = locationData[destinationCode]?.destinationUrl;

            if (!destinationUrl) {
              return of(null);
            }

            return this.buildDestinationPromo(flightParams, locationData, timetableInfo, picture, destinationUrl);
          })
        );
      })
    );
  }

  private promoPicture$(loadCmsPage: CmsTemplateLoader): Observable<CmsImageData | null> {
    return loadCmsPage(`/${this.languageService.langValue}/${OndStructurePagePath.CITY_TO_CITY_PROMO_IMAGE}`).pipe(
      map((template) => {
        const node = findFirstCmsNode(template?.main ?? [], (n) => {
          return n.localSettings?.contentType === DESTINATION_PROMO_IMAGE_CONTENT_TYPE;
        });
        return node?.picture ?? null;
      }),
      take(1),
      catchError(() => of(null)),
      defaultIfEmpty(null),
      finShare()
    );
  }

  private flightInfoPicture$(loadCmsPage: CmsTemplateLoader): Observable<CmsImageData | null> {
    return this.flightParams$.pipe(
      switchMap((flightParams) =>
        this.ondMarketingSeoService.getLocations(flightParams).pipe(
          take(1),
          switchMap((locationData) => {
            const picture = locationData?.[flightParams.destination]?.picture;

            if (picture) {
              return of(picture);
            }

            return this.loadCampaignPicture$(loadCmsPage);
          }),
          catchError(() => of(null)),
          defaultIfEmpty(null)
        )
      ),
      finShare()
    );
  }

  private loadCampaignPicture$(loadCmsPage: CmsTemplateLoader): Observable<CmsImageData | null> {
    return loadCmsPage(`/${this.languageService.langValue}`).pipe(
      map((template) => template?.main ?? []),
      findByType(CmsViewType.FRONT_PAGE_OFFER, false),
      map((campaign: CmsContent) => campaign?.picture || null),
      take(1),
      catchError(() => of(null))
    );
  }

  private patchNodes(
    nodes: CmsContent[],
    flightParams: FlightPair,
    heading: string,
    tripType: TripType,
    haul: HaulType | null,
    destinationPromo: CmsTeaser | null,
    faqs: CmsContent[],
    flightInfoPicture: CmsImageData | null,
    pricesAvailable: boolean | null,
    bluewings: CmsTeaserCard[]
  ): CmsContent[] {
    return (nodes ?? [])
      .filter((node) => this.shouldKeepNode(node, haul, destinationPromo, faqs, bluewings))
      .map((node) =>
        this.reusePatchedNode(
          node,
          flightParams,
          heading,
          tripType,
          haul,
          destinationPromo,
          faqs,
          flightInfoPicture,
          pricesAvailable,
          bluewings
        )
      );
  }

  private isDestinationPromoNode(node: CmsContent): boolean {
    return node.localSettings?.blockType === StructurePageBlockType.DESTINATION_PROMOTION;
  }

  private shouldKeepNode(
    node: CmsContent,
    haul: HaulType | null,
    destinationPromo: CmsTeaser | null,
    faqs: CmsContent[],
    bluewings: CmsTeaserCard[]
  ): boolean {
    switch (node.viewType) {
      case CmsViewType.OND_FAQS:
        return faqs.length > 0;
      case CmsViewType.OND_BLUEWINGS:
        return bluewings.length > 0;
      case CmsViewType.MARKETING_ACTIVITY_BLOCKS: {
        if (this.isDestinationPromoNode(node)) {
          return destinationPromo !== null;
        }
        const haulType = node.localSettings?.haulType;
        if (haulType !== HaulType.SHORT && haulType !== HaulType.LONG) {
          return true;
        }
        return haulType === haul;
      }
      default:
        return true;
    }
  }

  // Returns the previous object when unchanged so *ngFor (tracked by identity) keeps the block instead of re-creating it.
  private reusePatchedNode(
    node: CmsContent,
    flightParams: FlightPair,
    heading: string,
    tripType: TripType,
    haul: HaulType | null,
    destinationPromo: CmsTeaser | null,
    faqs: CmsContent[],
    flightInfoPicture: CmsImageData | null,
    pricesAvailable: boolean | null,
    bluewings: CmsTeaserCard[]
  ): CmsContent {
    const patched = this.buildPatchedNode(
      node,
      flightParams,
      heading,
      tripType,
      haul,
      destinationPromo,
      faqs,
      flightInfoPicture,
      pricesAvailable,
      bluewings
    );
    const previous = this.patchedNodes.get(node);
    if (previous && isDeepEqual(previous, patched)) {
      return previous;
    }
    this.patchedNodes.set(node, patched);
    return patched;
  }

  private buildPatchedNode(
    node: CmsContent,
    flightParams: FlightPair,
    heading: string,
    tripType: TripType,
    haul: HaulType | null,
    destinationPromo: CmsTeaser | null,
    faqs: CmsContent[],
    flightInfoPicture: CmsImageData | null,
    pricesAvailable: boolean | null,
    bluewings: CmsTeaserCard[]
  ): CmsContent {
    let patched: CmsContent = node;

    switch (node.viewType) {
      case CmsViewType.OND_FAQS:
        patched = { ...node, items: faqs } as CmsContent;
        break;
      case CmsViewType.OND_BLUEWINGS:
        patched = { ...node, items: bluewings } as CmsContent;
        break;
      case CmsViewType.DECORATED_HEADING:
        patched = { ...node, teaserTitle: heading };
        break;
      case CmsViewType.FULL_WIDTH_BOOKING_WIDGET:
        patched = {
          ...node,
          origin: { defaultLocation: { locationCode: flightParams.origin } },
          destination: { defaultLocation: { locationCode: flightParams.destination } },
          localSettings: {
            ...node.localSettings,
            initialTripType: tripType,
            initialTravelClass: globalBookingInitialState.travelClasses.selected,
            initialPaxAmount: globalBookingInitialState.paxAmount,
            disableOnDSelection: Boolean(node.localSettings?.disableOnDSelection),
          },
        } as CmsContent;
        break;
      case CmsViewType.DESTINATION_FLIGHT_INFO:
        patched = {
          ...node,
          picture: flightInfoPicture,
          localSettings: { ...node.localSettings, initialTripType: tripType, pricesAvailable },
        } as CmsContent;
        break;
      case CmsViewType.CALENDAR_SCHEDULE:
        patched = {
          ...node,
          localSettings: { ...node.localSettings, initialTripType: tripType, pricesAvailable },
        };
        break;
      case CmsViewType.MARKETING_ACTIVITY_BLOCKS:
        if (this.isDestinationPromoNode(node)) {
          patched = { ...node, items: [destinationPromo] } as CmsContent;
        }
        break;
    }

    if (Array.isArray(patched.items)) {
      patched = {
        ...patched,
        items: this.patchNodes(
          patched.items,
          flightParams,
          heading,
          tripType,
          haul,
          destinationPromo,
          faqs,
          flightInfoPicture,
          pricesAvailable,
          bluewings
        ),
      };
    }

    return patched;
  }

  private buildFaqItems(response: CityToCityPairResponse | null): CmsContent[] {
    const entities = response?.structuredData?.mainEntity ?? [];

    return entities.map(
      (entity, index) =>
        ({
          contentType: CmsContentType.CMTeaser,
          contentId: index,
          subjectTaxonomyTags: [],
          viewTypeName: null,
          teaserTitle: entity.name,
          teaserText: entity.acceptedAnswer?.text ?? '',
        }) as CmsTeaser
    );
  }

  private buildDestinationPromo(
    flightParams: FlightPair,
    locationData: OndLocationsResponse,
    timetableInfo: DestinationTimetableInfo,
    picture: CmsImageData | null,
    destinationUrl: string
  ): Observable<CmsTeaser | null> {
    return combineLatest([
      this.languageService.translate<DestinationPromotionCopy>('ondMarketing.destinationPromotion'),
      this.languageService.translate<string>('bookingSearch.duration'),
    ]).pipe(
      map(([copy, durationTemplate]) => {
        const { title, text } = buildDestinationPromoCopy(
          copy,
          durationTemplate,
          {
            origin: getLocationName(flightParams.origin, locationData),
            destination: getLocationName(flightParams.destination, locationData),
          },
          timetableInfo
        );

        if (!picture || !title || !text) {
          return null;
        }

        return {
          contentType: CmsContentType.CMTeaser,
          contentId: picture.contentId,
          subjectTaxonomyTags: [],
          viewTypeName: null,
          teaserTitle: title,
          teaserText: text,
          picture,
          target: { url: destinationUrl, external: false },
        } as CmsTeaser;
      })
    );
  }
}
